import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, useContainerWidth, Avatar } from '@fs/zion-ui'
import { number, shape, string, bool, func } from 'prop-types'
import { css } from '@emotion/core'
import { trackEvent } from '@fs/zion-analytics'
import { getDisplayName } from '../shared/helpers/name_formatting'
import { determineAncestorReferenceLabel } from './helpers/ancestor_Determinant'
import { determineGender } from '../shared/helpers/gedcom_X'
import { useTreeDispatch } from './state/TreeStore'

export default function AncestorCard({ disabled, personData, setIsTreeShowing, showRoman }) {
  const atWidth = useContainerWidth()
  const [t] = useTranslation()
  const [imageError, setImageError] = useState(false)
  const treeDispatch = useTreeDispatch()

  const cardViewCss = css`
    ${determineGender(personData.genderType) === 'male' && 'border-top: 6px solid #01b6d1'};
    ${determineGender(personData.genderType) === 'female' && 'border-top: 6px solid #bd0057'};
    ${determineGender(personData.genderType) === 'unknown' && 'border-top: 6px solid #aaa'};
    ${atWidth({ default: 'width: calc(100vw / 5.25)', md: 'width: 120px' })};
    ${atWidth({ default: 'height: calc(100vw / 3)', md: 'height: 175px' })};
    a,
    a:visited {
      text-decoration: none;
      color: #177c9c;
    }
  `
  const squareCardCss = css`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    background-color: ${colors.gray90};
    border: none;
    outline: none;
    &:hover {
      background-color: ${colors.gray70};
      ${disabled ? 'cursor: not-allowed' : 'cursor: pointer'};
    }
  `

  const avatarImgCss = css`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    ${atWidth({
      default: `
        width: 40px;
        height: 40px;
      `,
      lg: `
        width: 88px;
        height: 88px;
      `,
    })};
  `

  const lifeSpanCss = css`
    color: gray;
    ${atWidth({ default: 'display: none;', md: 'font-size: 10px;' })};
  `

  const nameCss = css`
    font-weight: bold;
    text-overflow: ellipsis;
    color: ${colors.gray02};
    overflow: hidden;
    ${atWidth({
      default: 'font-size: 10px;',
      md: 'font-size: 12px; padding-bottom: 4px; padding-top: 4px;',
      lg: 'font-size: 13px; padding-bottom: 8px; padding-top: 4px;',
    })};
  `

  const ancestorItem = css`
    flex: 1;
    ${atWidth({ default: 'margin-top: none', md: 'margin-top: 10px' })};
  `
  const ancestorImg = css`
    display: flex;
    align-items: center;
  `

  const determineTokenizedIdentifier = () => {
    const tokenSet = determineAncestorReferenceLabel(personData.atn, personData.genderType)
    return t(tokenSet.key, tokenSet.defaultValue)
  }

  const buildName = () => {
    if (personData.displayName) {
      return personData.displayName
    }

    return determineTokenizedIdentifier()
  }

  const handleClick = () => {
    trackEvent({ event_name: 'AncestorCard:SELECT_ANCESTOR:CLICK' })
    treeDispatch({
      type: 'EDIT_ANCESTOR',
      anchorATN: personData.atn,
    })
    setIsTreeShowing(false)
  }

  return (
    personData && (
      <div css={cardViewCss}>
        <button css={squareCardCss} type="button" disabled={disabled} onClick={handleClick}>
          <div css={[ancestorItem, ancestorImg]}>
            {personData?.portraitThumbnailLink && !imageError ? (
              <img
                css={avatarImgCss}
                alt="placeholder"
                src={personData.portraitThumbnailLink}
                onError={() => setImageError(true)}
              />
            ) : (
              <Avatar size={atWidth({ default: 'md', lg: 'lg' })} sex={determineGender(personData?.genderType)} />
            )}
          </div>
          <div css={ancestorItem}>
            <div css={nameCss}>
              <span>{personData.name && personData.surname ? getDisplayName(personData, showRoman) : buildName()}</span>
            </div>
            <div css={lifeSpanCss}>{personData.lifespan || ''}</div>
          </div>
        </button>
      </div>
    )
  )
}

AncestorCard.propTypes = {
  disabled: bool,
  personData: shape({
    name: string,
    atn: number,
    surname: string,
    displayName: string,
    genderType: string,
    portraitThumbnailLink: string,
    lifespan: string,
  }),
  setIsTreeShowing: func,
}
